/**
 * Single registry of feature gate names, shared by every consumer in the
 * monorepo — backend services (`@orn/api`, `@orn/core`, `@orn/mops-api`)
 * and frontend apps (`@orn/web`, `@orn/mops-web`).
 *
 * **Why this exists:** every feature gate reference — server-side
 * `FeatureGateController.gatePasses(vc, name)` or client-side
 * `useFeatureGate` / `flags.has(...)` — must round-trip through this
 * registry instead of being passed as a bare string literal. Two reasons:
 *
 *  1. **Typo-proof.** Callers consume {@link FeatureGateName}, so
 *     `useFeatureGate('feat-assitant')` (typo) is a compile error rather
 *     than a silent always-false at runtime.
 *  2. **Discoverability.** Every gate the codebase cares about lives in
 *     this one file — operators reading the code can see the full set
 *     without grepping.
 *
 * This package is a leaf with no workspace or runtime dependencies, so
 * both Node and browser bundles can import it without dragging in
 * backend or frontend code ([ORN-850](https://linear.app/go-mantle/issue/ORN-850)
 * tracked the consolidation from per-package mirrors to this single
 * source of truth).
 *
 * **Naming conventions** for the *string values* are documented in
 * `packages/core/docs/feature-gate-naming.md`. TL;DR: lowercase +
 * hyphens, prefix with `feat-` / `kill-` / `exp-` / `ops-` / `debug-`,
 * names are public on the wire and immutable after creation.
 *
 * **Adding a gate:** append a new member here, then create the gate row
 * in mops-web's `NewFeatureGateScene` with the matching `name`. The DB
 * row is the runtime source of truth; this file is the *type*-time
 * source of truth for which names are legal in source code.
 *
 * **Removing a gate:** delete every consumer call site first, then the
 * registry entry, then the DB row. Order matters — an orphaned consumer
 * reading a deleted gate fails closed (returns `false`), which is
 * usually fine but worth doing in the right order so you never ship an
 * intermediate state where the gate is "gone" from the registry but
 * still referenced.
 */
export const FeatureGate = {
  /**
   * Gates the consumer "Bills & Subscriptions" sidebar tab. Internal
   * employees only at launch ([ORN-690](https://linear.app/go-mantle/issue/ORN-690)).
   * Eventual graduation: rule swap → percent rollout → `ALL_USERS` via
   * mops-web's New/Edit Feature Gate scenes.
   */
  Obligations: 'feat-obligations',
  /**
   * Gates the top-level "Mantle Assistant" nav button (and the
   * `/assistant` route) and the `/chat` WebSocket on `@orn/api`. Internal
   * employees only at launch ([ORN-692](https://linear.app/go-mantle/issue/ORN-692)).
   * Eventual graduation: rule swap → percent rollout → `ALL_USERS` via
   * mops-web's New/Edit Feature Gate scenes.
   */
  Assistant: 'feat-assistant',
  /**
   * Gates the v2 onboarding flow (umbrella initiative
   * [ORN-575](https://linear.app/go-mantle/issue/ORN-575)). Internal
   * employees only at launch. Eventual graduation: rule swap → percent
   * rollout → `ALL_USERS` via mops-web's New/Edit Feature Gate scenes.
   */
  OnboardingV2: 'feat-onboarding-v2',
  /**
   * Gates the avatar-dropdown `Debug` submenu and the `/debug/*` routes
   * (`Sentry test`, `Environment`). Internal employees only —
   * `IS_ADMIN` rule at launch ([ORN-691](https://linear.app/go-mantle/issue/ORN-691)).
   * Per the `debug-` prefix convention, this gate is expected to stay
   * pinned to internal audiences rather than graduating to `ALL_USERS`.
   */
  DebugTools: 'debug-tools',
  /**
   * Gates the `JsonDebugRow` inside transaction-family slideouts (Bill,
   * RecurringTransaction, Transaction). Replaces the old `User.isAdmin`
   * client-side check so gating is operator-controlled via mops-web rather
   * than hard-coded to the admin email list ([ORN-791](https://linear.app/go-mantle/issue/ORN-791)).
   * Per the `debug-` prefix convention, expected to stay pinned to internal
   * audiences rather than graduating to `ALL_USERS`.
   */
  DebugTransactionDetails: 'debug-transaction-details',
  /**
   * Gates the assistant's long-term memory pipeline — the write aggregates
   * (`memory_record` / `memory_invalidate` / `memory_update`),
   * the read aggregate (`memory_recall`), and the `MEMORY` system-prompt
   * block that injects recalled memories into the agent's context.
   *
   * When the gate is off, the assistant behaves identically to a stateless
   * chatbot: no memory tools appear in the agent surface, the system
   * prompt does not include a MEMORY block, and the memory tables
   * accumulate nothing.
   */
  AssistantMemory: 'feat-assistant-memory',
  /**
   * Gates the assistant chat slash-command surface. When off, a leading
   * `/issue …` is treated as ordinary chat text and flows to the LLM — the
   * orchestrator only diverts a command when this gate passes. Internal testers
   * / dogfooders only; the `/issue` flow collects internal feedback on
   * assistant responses, so it is not expected to graduate to `ALL_USERS`.
   */
  AssistantCommands: 'feat-assistant-commands',
  /**
   * Kill switch for the **synchronous** thread-compaction call on a
   * message-cap rollover. When ON, `bindAssistantSession` skips the
   * inline `compactThread` await on reconnect — the prior thread's
   * summary is left to the hourly async sweep instead. Use during an
   * Anthropic outage / latency spike, when blocking the bind path is
   * worse than deferring the summary.
   *
   * Default OFF (compaction runs). Fails closed to "runs" — an absent
   * or disabled gate row leaves the synchronous path active; an
   * operator enables this gate only to deliberately suspend it. The
   * async sweep has no equivalent gate: it runs cross-tenant under
   * `AsyncJobViewerContext` (no user to evaluate rules against), so it
   * is halted by disabling its EventBridge rule instead.
   */
  KillAssistantCompactionSync: 'kill-assistant-compaction-sync',
  /**
   * Internal-debug toggle for the assistant chat surface. When on, the server
   * includes each tool's name / args / output on the live `tool_started` /
   * `tool_completed` frames and the web client logs them to the browser
   * console in real time instead of rendering them — the consumer UI shows
   * only a spinner while a turn is in flight ([ORN-1342](https://linear.app/go-mantle/issue/ORN-1342)).
   * Internal employees only per the `debug-` prefix convention; not expected
   * to graduate to `ALL_USERS`.
   */
  DebugAssistant: 'debug-assistant',
  /**
   * Gates proactive assistant SMS outreach when new callouts open for a
   * family.
   * Experiment / prototype: internal employees only at launch (`is_admin`
   * rule) until notification settings, opt-out, and frequency controls
   * exist. Eventual graduation via mops-web's New/Edit Feature Gate scenes.
   */
  AssistantNotifications: 'exp-assistant-notifications',
} as const;

/**
 * Union of every value in {@link FeatureGate}. Pass this type to
 * `useFeatureGate` / `FeatureGateController.gatePasses` so unregistered
 * string literals fail type-check at the call site.
 */
export type FeatureGateName = (typeof FeatureGate)[keyof typeof FeatureGate];

/**
 * Dev-only resolution table for every registered feature gate. Consumed
 * by `FeatureGateController` (`@orn/core`) — both `gatePasses` and
 * `getEnabledGateNamesForViewer` — only when the server's `IS_DEV` flag
 * is `true`. In dev this map is **the sole source of truth**: the DB is
 * not consulted, so a gate doesn't need (and can't get) a `feature_gates`
 * row to evaluate true locally. In every non-dev environment (`test`,
 * `staging`, `prod`) the map is ignored entirely and gate evaluation
 * runs against `feature_gates` as before.
 *
 * **Why server-side.** Dev defaults live on the backend so there is one
 * place to manage them and the client can stay dumb — `useFeatureGate`
 * just reads what `enabledFeatureGates` returns. That keeps Storybook
 * predictable too: stories control gate state via their MSW handlers,
 * not via a parallel client-side override path.
 *
 * **Why non-`Partial`.** Every registered gate must declare its dev
 * default explicitly. Forgetting to add a new gate here is a compile
 * error rather than a silent fall-through, which used to be a common
 * tripping point ("oh man I forgot to add my gate here again"). To flip
 * a gate during testing, edit its entry below — the map is the dial.
 */
export const FEATURE_GATE_DEV_DEFAULTS: Record<FeatureGateName, boolean> = {
  [FeatureGate.Obligations]: true,
  [FeatureGate.Assistant]: true,
  [FeatureGate.OnboardingV2]: true,
  [FeatureGate.DebugTools]: true,
  [FeatureGate.DebugTransactionDetails]: true,
  // On in dev defaults so manual chat sessions exercise the full record →
  // recall loop (write aggregates + `memory_recall` + the MEMORY
  // system-prompt block). Eval baselines are unaffected: the eval runner
  // does not pass `assistantMemoryEnabled` to the orchestrator, so the
  // memory tools never reach the eval surface regardless of this
  // value. Production rollout is controlled DB-side via the `feature_gates`
  // table, not this dev-only map.
  [FeatureGate.AssistantMemory]: true,
  // On in dev defaults so manual chat sessions can exercise `/issue` end to
  // end. Production rollout is DB-side via the `feature_gates` table.
  [FeatureGate.AssistantCommands]: true,
  // Kill switch defaults OFF in dev so local message-cap rollovers
  // exercise the real synchronous compaction path. Flip to `true` to
  // simulate the suspended state.
  [FeatureGate.KillAssistantCompactionSync]: false,
  // On in dev so local chat sessions surface tool-call details in the
  // console — the inline tool UI is gone, so this is the dev affordance for
  // inspecting what the assistant ran. Production rollout is DB-side.
  [FeatureGate.DebugAssistant]: true,
  // On in dev so local non-initial sync flows exercise the proactive
  // outreach path end to end. Production rollout is DB-side via the
  // `feature_gates` table (employee-only `is_admin` rule at launch).
  [FeatureGate.AssistantNotifications]: true,
};
